//region header */
//--------------------------------------------------------------------------------------------------
//
//  Description: Property Page
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   20.07.21 Sean Flook          WI39??? Initial Revision.
//    002   07.09.23 Sean Flook                  Cleaned the code.
//    003   02.01.24 Sean Flook                  Changed console.log to console.error for error messages.
//    004   04.04.24 Sean Flook                  Added better handling of API return status.
//    005   05.04.24 Sean Flook                  Correctly handle errors when getting a property.
//    006   05.04.24 Sean Flook        IMANN-351 Changes to handle browser navigation.
//    007   11.04.24 Sean Flook        IMANN-351 Prevent infinite loops when creating a new record.
//    008   18.04.24 Sean Flook        IMANN-351 Changes required to reload the contexts after a refresh.
//    009   18.04.24 Sean Flook        IMANN-351 Corrected typo.
//    010   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    011   24.06.24 Sean Flook        IMANN-170 Changes required for cascading parent PAO changes to children.
//    012   05.09.24 Sean Flook        IMANN-575 Added additional debug message.
//    013   10.09.24 Sean Flook        IMANN-980 Only write to the console if the user has the showMessages right.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    014   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    015   30.01.25 Sean Flook       IMANN-1673 Changes required for new user settings API.
//    016   23.04.25 Joshua McCormick IMANN-1743 Commented out all sessionStorage calls
//    017   24.04.25 Sean Flook       IMANN-1760 Reenabled above change as required.
//endregion Version 1.0.5.0
//region Version 1.0.7.0
//    018   13.06.25 Sean Flook       IMANN-1812 Modified to use the new Sandbox store.
//    019   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    020   16.09.25 Sean Flook       IMANN-1963 Use getPropertyExtent to get the extent of the property including the provenances.
//    021   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    022   27.10.25 Sean Flook       IMANN-2111 Added react-resizable-panels.
//    023   28.10.25 Sean Flook       IMANN-2111 Change colour of resize handles when hovering cursor over them.
//    024   06.11.25 Sean Flook       IMANN-2092 Correctly set the spatialReferenceWkid variable.
//    025   17.12.25 Sean Flook       IMANN-1705 Set mapContext.useSearchExtentChange to true when loading a property so that the map uses the property extent when loading.
//    026   24.03.26 Sean Flook       IMANN-2369 Only use the startExtent if required.
//    027   30.03.26 Sean Flook       IMANN-2377 If creating a new property, use the current map extent as the startExtent.
//    028   07.04.26 Sean Flook       IMANN-2395 Added timeouts to ensure data is loaded correctly.
//endregion Version 1.0.7.0
//region Version 1.0.7.1
//    029   27.04.26 Sean Flook       IMANN-2411 Correctly compare UPRNs.
//    030   28.04.26 Sean Flook       IMANN-2415 Removed useSearchExtent as not required.
//endregion Version 1.0.7.1
//
//--------------------------------------------------------------------------------------------------
//endregion header */

//region imports

import React, { useContext, useState, useRef, useEffect } from "react";
import { useLocation } from "react-router";
import useSandboxStore from "../context/SandboxStore";

import PropertyContext from "../context/propertyContext";
import UserContext from "./../context/userContext";
import MapContext from "../context/mapContext";
import SettingsContext from "../context/settingsContext";
import StreetContext from "../context/streetContext";
import LookupContext from "../context/lookupContext";
import SearchContext from "../context/searchContext";
import InformationContext from "../context/informationContext";

import { GetPropertyFromUPRNUrl } from "../configuration/ADSConfig";
import { UpdateSandbox } from "../utils/HelperUtils";
import { GetNewProperty, getPropertyExtent } from "../utils/PropertyUtils";
import { PropertyRoute } from "../PageRouting";

import { Box } from "@mui/material";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import PropertyDataForm from "../forms/PropertyDataForm";

import ADSMap from "../components/ADSMap";
import { EditConfirmationServiceProvider } from "./EditConfirmationPage";

import { dataFormStyle } from "../utils/ADSStyles";
import { adsLightGreyD } from "../utils/ADSColours";

//endregion imports

function PropertyPage() {
  const sandboxStore = useSandboxStore((state) => state);
  const sourceProperty = useSandboxStore((state) => state.sourceProperty);
  const setStore = useSandboxStore((state) => state.setStore);

  const propertyContext = useContext(PropertyContext);
  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);
  const settingsContext = useContext(SettingsContext);
  const streetContext = useContext(StreetContext);
  const lookupContext = useContext(LookupContext);
  const searchContext = useContext(SearchContext);
  const informationContext = useContext(InformationContext);

  const location = useLocation();

  const [apiUrl, setApiUrl] = useState(null);
  const [data, setData] = useState();
  const dataUprn = useRef(-1);
  const [loading, setLoading] = useState(false);
  const [reloadContexts, setReloadContexts] = useState(false);
  const [propertyExtent, setPropertyExtent] = useState(null);

  const spatialReferenceWkid = useRef(mapContext.spatialReference || 27700);

  useEffect(() => {
    if (sessionStorage.getItem("PropertyPage_firstLoadDone") === null) {
      sessionStorage.setItem("PropertyPage_firstLoadDone", 1);
    } else {
      setReloadContexts(true);
    }
  }, []);

  useEffect(() => {
    if (reloadContexts) {
      setReloadContexts(false);

      // Reload user context first
      userContext.onReload();

      // Then reload settings context (depends on user context)
      setTimeout(() => {
        settingsContext.onReload();

        // Then reload search contexts (depends on user and settings contexts)
        setTimeout(() => {
          searchContext.onReload();

          // Finally reload all other contexts (depend on user, settings and search contexts)
          setTimeout(() => {
            propertyContext.onReload();
            streetContext.onReload();
            lookupContext.onReload();
            informationContext.onReload();
            mapContext.onReload();
          }, 0);
        }, 0);
      }, 0);
    }
  }, [
    reloadContexts,
    userContext,
    propertyContext,
    mapContext,
    settingsContext,
    streetContext,
    lookupContext,
    searchContext,
    informationContext,
  ]);

  useEffect(() => {
    function SetUpPropertyData(urlUprn) {
      if (urlUprn && Number(urlUprn) !== 0) {
        if (apiUrl) {
          if (Number(urlUprn) !== Number(dataUprn.current)) {
            dataUprn.current = urlUprn;
            setLoading(true);
            fetch(`${apiUrl.url}/${urlUprn}`, {
              headers: apiUrl.headers,
              crossDomain: true,
              method: apiUrl.type,
            })
              .then((res) => (res.ok ? res : Promise.reject(res)))
              .then((res) => {
                switch (res.status) {
                  case 200:
                    return res.json();

                  case 204:
                    propertyContext.onPropertyErrors(
                      [
                        {
                          field: "UPRN",
                          errors: ["This property no longer exists in the database."],
                        },
                      ],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      0
                    );
                    return null;

                  case 401:
                    userContext.onExpired();
                    return null;

                  case 403:
                    propertyContext.onPropertyErrors(
                      [
                        {
                          field: "UPRN",
                          errors: ["You do not have database access."],
                        },
                      ],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      0
                    );
                    return null;

                  case 500:
                    propertyContext.onPropertyErrors(
                      [
                        {
                          field: "UPRN",
                          errors: ["Unexpected server error, please report to support."],
                        },
                      ],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      0
                    );
                    if (userContext.currentUser.showMessages)
                      console.error("[500 ERROR] GetPropertyMapData: Unexpected server error.", res);
                    return null;

                  default:
                    propertyContext.onPropertyErrors(
                      [
                        {
                          field: "UPRN",
                          errors: ["Unexpected error, please report to support."],
                        },
                      ],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      0
                    );
                    if (userContext.currentUser.showMessages)
                      console.error(`[${res.status} ERROR] GetPropertyMapData: Unexpected error.`, res);
                    return null;
                }
              })
              .then(
                (result) => {
                  if (userContext.currentUser.showMessages)
                    console.log("[DEBUG] SetUpPropertyData", apiUrl, urlUprn, JSON.stringify(result));
                  setData(result);
                  if (
                    urlUprn &&
                    (!propertyContext.currentProperty ||
                      Number(propertyContext.currentProperty.uprn) !== Number(urlUprn))
                  ) {
                    propertyContext.onUpdateCurrentProperty(
                      result.uprn,
                      result.lpis && result.lpis.length ? result.lpis[0].usrn : 0,
                      result.lpis && result.lpis.length
                        ? result.lpis.filter((x) => x.language === "ENG")[0].address
                        : "",
                      result.lpis && result.lpis.length
                        ? result.lpis.filter((x) => x.language === "ENG")[0].address
                        : "",
                      result.lpis && result.lpis.length
                        ? result.lpis.filter((x) => x.language === "ENG")[0].postcode
                        : "",
                      result.xcoordinate,
                      result.ycoordinate,
                      false,
                      null
                    );
                  }
                  propertyContext.onChildCountChange(result.childCount);
                  setStore(UpdateSandbox(sandboxStore, "sourceProperty", result, "allProperty"));
                  setPropertyExtent(getPropertyExtent(result, spatialReferenceWkid.current));
                },
                (error) => {
                  if (userContext.currentUser.showMessages) console.error("[ERROR] Get Property data", error);
                }
              )
              .then(() => {
                setLoading(false);
              });
          }
        }
      } else {
        if (urlUprn && Number(urlUprn) === 0 && Number(dataUprn.current) !== 0) {
          setLoading(true);

          if (sourceProperty) {
            setData(sourceProperty);
            setPropertyExtent(getPropertyExtent(sourceProperty, spatialReferenceWkid.current));
          } else {
            const newProperty = GetNewProperty(
              settingsContext.isWelsh,
              settingsContext.isScottish,
              settingsContext.authorityCode,
              propertyContext.currentProperty.usrn,
              propertyContext.currentProperty.parent,
              propertyContext.currentProperty.easting,
              propertyContext.currentProperty.northing
            );
            setData(newProperty);
            setPropertyExtent(getPropertyExtent(newProperty, spatialReferenceWkid.current));
            if (
              urlUprn &&
              (!propertyContext.currentProperty || Number(propertyContext.currentProperty.uprn) !== Number(urlUprn))
            )
              propertyContext.onUpdateCurrentProperty(
                0,
                propertyContext.currentProperty.usrn,
                newProperty.lpis.filter((x) => x.language === "ENG")[0].address,
                newProperty.lpis.filter((x) => x.language === "ENG")[0].address,
                newProperty.lpis.filter((x) => x.language === "ENG")[0].postcode,
                newProperty.xcoordinate,
                newProperty.ycoordinate,
                true,
                null
              );
            propertyContext.onChildCountChange(0);
            setStore(UpdateSandbox(sandboxStore, "sourceProperty", newProperty, "allProperty"));
          }
          dataUprn.current = "0";
          setLoading(false);
        }
      }
    }

    if (!apiUrl && userContext.currentUser && settingsContext.authorityCode > 0) {
      const propertyUrl = GetPropertyFromUPRNUrl(userContext.currentUser);
      setApiUrl(propertyUrl);
    }

    if (location.pathname.includes(PropertyRoute)) {
      const urlUprn = location.pathname.replace(`${PropertyRoute}/`, "");
      if (Number(urlUprn) !== Number(dataUprn.current)) SetUpPropertyData(urlUprn);
    }

    return () => {};
  }, [
    propertyContext,
    userContext,
    settingsContext,
    mapContext,
    apiUrl,
    location,
    sourceProperty,
    setStore,
    sandboxStore,
  ]);

  return (
    <EditConfirmationServiceProvider>
      <Box sx={dataFormStyle("ResizePanels")}>
        <PanelGroup autoSaveId="propertyPagePanels" direction="horizontal">
          <Panel id="propertyDataFormPanel" defaultSize={35} minSize={30} order={1}>
            <PropertyDataForm data={data} loading={loading} />
          </Panel>
          <PanelResizeHandle>
            <Box
              sx={{
                width: "5px",
                cursor: "col-resize",
                height: "100%",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: adsLightGreyD,
                },
              }}
            />
          </PanelResizeHandle>
          <Panel id="propertyMapPanel" defaultSize={65} minSize={30} order={2}>
            <ADSMap
              mapType="property"
              startExtent={
                propertyExtent
                  ? propertyExtent
                  : propertyContext.currentProperty?.newProperty && mapContext.currentExtent
                    ? {
                        xmin: mapContext.currentExtent.xmin,
                        ymin: mapContext.currentExtent.ymin,
                        xmax: mapContext.currentExtent.xmax,
                        ymax: mapContext.currentExtent.ymax,
                        spatialReference: mapContext.currentExtent.spatialReference,
                      }
                    : null
              }
            />
          </Panel>
        </PanelGroup>
      </Box>
    </EditConfirmationServiceProvider>
  );
}

export default PropertyPage;
